import { useMemo } from 'react';

/**
 * Card devocional exibido durante a recarga — um convite a 1 minuto de oração
 * acompanhado de um versículo bíblico. A Bow é uma empresa cristã e este
 * espaço de espera (vários minutos recarregando) é uma oportunidade natural
 * de pausa e reflexão.
 *
 * Seleção determinística por dia do ano (mesmo versículo pro mesmo dia) pra
 * dar variedade sem persistir estado. Atualiza ao abrir o app em novo dia.
 *
 * `tone="dark"` é usado dentro do ActiveCharge cinematográfico (fundo navy
 * + aurora) — vidro translúcido com texto cream em vez de cream com texto
 * navy.
 */

interface Verse {
  text: string;
  ref: string;
}

interface Props {
  tone?: 'light' | 'dark';
}

const VERSES: Verse[] = [
  { text: 'O Senhor é o meu pastor; nada me faltará.', ref: 'Salmos 23:1' },
  { text: 'Tudo posso naquele que me fortalece.', ref: 'Filipenses 4:13' },
  {
    text: 'Não andeis ansiosos de coisa alguma; em tudo, porém, sejam conhecidas, diante de Deus, as vossas petições, pela oração e pela súplica, com ações de graças.',
    ref: 'Filipenses 4:6'
  },
  { text: 'Deleita-te também no Senhor, e ele te concederá os desejos do teu coração.', ref: 'Salmos 37:4' },
  { text: 'Entrega o teu caminho ao Senhor; confia nele, e ele tudo fará.', ref: 'Salmos 37:5' },
  { text: 'A paz vos deixo, a minha paz vos dou; não vo-la dou como o mundo a dá.', ref: 'João 14:27' },
  {
    text: 'Porque Deus amou o mundo de tal maneira que deu o seu Filho unigênito, para que todo o que nele crê não pereça, mas tenha a vida eterna.',
    ref: 'João 3:16'
  },
  { text: 'Sejam fortes e corajosos. Não tenham medo nem fiquem apavorados, porque o Senhor, o seu Deus, estará com vocês por onde forem.', ref: 'Josué 1:9' },
  { text: 'Aquietai-vos, e sabei que eu sou Deus.', ref: 'Salmos 46:10' },
  { text: 'Lança o teu cuidado sobre o Senhor, e ele te susterá.', ref: 'Salmos 55:22' },
  { text: 'Mas os que esperam no Senhor renovarão as suas forças, subirão com asas como águias.', ref: 'Isaías 40:31' },
  {
    text: 'Confia no Senhor de todo o teu coração e não te estribes no teu próprio entendimento. Reconhece-o em todos os teus caminhos, e ele endireitará as tuas veredas.',
    ref: 'Provérbios 3:5-6'
  },
  { text: 'O Senhor é a minha luz e a minha salvação; a quem temerei?', ref: 'Salmos 27:1' },
  { text: 'Bem-aventurados os mansos, porque herdarão a terra.', ref: 'Mateus 5:5' },
  {
    text: 'E conhecereis a verdade, e a verdade vos libertará.',
    ref: 'João 8:32'
  },
  { text: 'O amor é paciente, o amor é bondoso. Não inveja, não se vangloria, não se orgulha.', ref: '1 Coríntios 13:4' },
  {
    text: 'Porque sou eu que conheço os planos que tenho para vocês, diz o Senhor, planos de fazê-los prosperar e não de causar dano, planos de dar-lhes esperança e um futuro.',
    ref: 'Jeremias 29:11'
  },
  { text: 'Vinde a mim, todos os que estais cansados e sobrecarregados, e eu vos aliviarei.', ref: 'Mateus 11:28' },
  { text: 'Grande é o Senhor e mui digno de ser louvado; e a sua grandeza é inescrutável.', ref: 'Salmos 145:3' },
  { text: 'Alegrai-vos sempre no Senhor; outra vez digo, alegrai-vos.', ref: 'Filipenses 4:4' },
  {
    text: 'E sabemos que todas as coisas cooperam para o bem daqueles que amam a Deus, daqueles que são chamados segundo o seu propósito.',
    ref: 'Romanos 8:28'
  },
  { text: 'O Senhor é o meu refúgio e a minha fortaleza, o meu Deus em quem confio.', ref: 'Salmos 91:2' },
  { text: 'Bem-aventurado o homem que não anda segundo o conselho dos ímpios.', ref: 'Salmos 1:1' },
  { text: 'Examina-me, ó Deus, e conhece o meu coração; prova-me e conhece os meus pensamentos.', ref: 'Salmos 139:23' },
  { text: 'Tua palavra é lâmpada para os meus pés e luz para o meu caminho.', ref: 'Salmos 119:105' },
  { text: 'O Senhor peleja por vós, e vós vos calareis.', ref: 'Êxodo 14:14' },
  { text: 'Se Deus é por nós, quem será contra nós?', ref: 'Romanos 8:31' },
  { text: 'Buscai primeiro o Reino de Deus e a sua justiça, e todas estas coisas vos serão acrescentadas.', ref: 'Mateus 6:33' },
  {
    text: 'Dê graças em todas as circunstâncias, pois esta é a vontade de Deus para vocês em Cristo Jesus.',
    ref: '1 Tessalonicenses 5:18'
  },
  { text: 'Ainda que eu ande pelo vale da sombra da morte, não temerei mal algum, porque tu estás comigo.', ref: 'Salmos 23:4' }
];

function dayOfYear(d: Date): number {
  const start = new Date(d.getFullYear(), 0, 0);
  const diff = d.getTime() - start.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

export function DevotionalCard({ tone = 'light' }: Props) {
  const verse = useMemo<Verse>(() => {
    const idx = dayOfYear(new Date()) % VERSES.length;
    return VERSES[idx];
  }, []);

  if (tone === 'dark') {
    return (
      <div className="flex flex-col gap-2 rounded-3xl border border-bow-cream/10 bg-bow-cream/[0.06] p-5 backdrop-blur-md">
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-bow-cream/55">
          Enquanto carrega
        </span>
        <p className="text-sm text-bow-cream/85">
          Que tal tirar 1 minuto pra orar e meditar?
        </p>
        <blockquote className="border-l-2 border-bow-cream/30 pl-3 text-sm italic text-bow-cream/75">
          "{verse.text}"
          <footer className="mt-1 text-xs not-italic text-bow-cream/55">
            — {verse.ref}
          </footer>
        </blockquote>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded-3xl bg-white p-5 shadow-soft">
      <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-bow-royal/55">
        Enquanto carrega
      </span>
      <p className="text-sm text-bow-royal/85">
        Que tal tirar 1 minuto pra orar e meditar?
      </p>
      <blockquote className="border-l-2 border-bow-royal/25 pl-3 text-sm italic text-bow-royal/75">
        "{verse.text}"
        <footer className="mt-1 text-xs not-italic text-bow-royal/55">
          — {verse.ref}
        </footer>
      </blockquote>
    </div>
  );
}
